'use strict';

const libPictView = require('pict-view');
const libProvenanceObjectEditor = require('../config-editor/views/ProvenanceObjectEditor.js');

/**
 * ConfigViewer — the provenance-aware configuration viewer/editor. A left rail browses configuration
 * namespaces (cascading Configuration service) plus record CustomProperties; the centre renders the
 * effective merged object in a pict-section-objecteditor tree where every value carries a badge for the
 * scope that set it (Global / Customer / Project / User); a write-scope picker chooses where edits go;
 * a right panel explains what the namespace + its keys mean. All state lives in AppData.ConfigViewer and
 * all behaviour in the ConfigViewer provider; the view derives + repaints declaratively.
 */
class ConfigViewer extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
	}

	get provider()
	{
		return this.pict.providers.ConfigViewer;
	}

	onBeforeRender(pRenderable)
	{
		if (this.provider) { this.provider.deriveRenderState(); }
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		this.pict.CSSMap.injectCSS();
		this._mountEditor();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	/** (Re)mount the embedded provenance editor after each paint (the main render replaces the host). */
	_mountEditor()
	{
		if (this.pict.ContentAssignment.getElement('#CV-EditorHost').length === 0) { return; }
		const tmpView = this._ensureEditorView();
		if (!tmpView) { return; }
		tmpView.options.Editable = !!(this.provider && this.provider.isEditable);
		tmpView.options.DefaultDestinationAddress = '#CV-EditorHost';
		tmpView.render();
	}

	/** Lazily register the ProvenanceObjectEditor bound to the live EditingConfig + ProvenanceMap. */
	_ensureEditorView()
	{
		const tmpHash = 'CV-ConfigEditor';
		if (this.pict.views[tmpHash]) { return this.pict.views[tmpHash]; }
		this.pict.addView(tmpHash, Object.assign({}, libProvenanceObjectEditor.default_configuration,
			{
				ViewIdentifier: tmpHash,
				AutoRender: false,
				Editable: false,
				InitialExpandDepth: 3,
				ObjectDataAddress: 'AppData.ConfigViewer.EditingConfig',
				ProvenanceDataAddress: 'AppData.ConfigViewer.ProvenanceMap',
				Renderables:
				[
					{
						RenderableHash: 'ObjectEditor-Container',
						TemplateHash: 'ObjectEditor-Container-Template',
						DestinationAddress: '#CV-EditorHost',
						RenderMethod: 'replace',
					}
				],
			}), libProvenanceObjectEditor);
		const tmpView = this.pict.views[tmpHash];
		// The editor builds its node renderers in onBeforeInitialize; a view added post-boot must be
		// initialized explicitly before its first render (same gotcha as ProjectWorkspace's embed).
		if (tmpView && typeof tmpView.initialize === 'function') { tmpView.initialize(); }
		return tmpView;
	}

	// --- inline handlers (orchestrate provider async + repaint) ---

	_repaint()
	{
		this.render();
	}

	_toast(pMessage, pType)
	{
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		if (tmpModal && typeof tmpModal.toast === 'function')
		{
			tmpModal.toast(pMessage, { type: pType || 'success' });
		}
	}

	selectRailItem(pKind, pNamespaceKey, pEntity, pRecordID)
	{
		if (pKind === 'customprops')
		{
			this.provider.selectCustomPropertiesAsync(pEntity, parseInt(pRecordID, 10), `${pEntity} CustomProperties`)
				.then(() => this._repaint());
			return;
		}
		this.provider.selectNamespaceAsync(pNamespaceKey).then(() => this._repaint());
	}

	selectValue(pValue)
	{
		const tmpSelected = this.provider.state.Selected;
		if (!tmpSelected) { return; }
		this.provider.selectNamespaceAsync(tmpSelected.NamespaceKey, pValue).then(() => this._repaint());
	}

	setWriteScope(pScope)
	{
		this.provider.setWriteScope(pScope);
		this._repaint();
	}

	save()
	{
		const tmpProvider = this.provider;
		const tmpSelected = tmpProvider.state.Selected;
		if (!tmpSelected) { return; }
		const fDone = (pMessage) => { tmpProvider.state.Message = ''; this._repaint(); this._toast(pMessage); };
		const fFail = (pError) => { tmpProvider.state.Message = `Save failed: ${pError.message || pError}`; this._repaint(); this._toast('Save failed', 'error'); };
		if (tmpSelected.Mode === 'customprops')
		{
			tmpProvider.saveCustomPropertiesAsync(tmpSelected.Entity, tmpSelected.RecordID).then(() => fDone('Saved.')).catch(fFail);
		}
		else
		{
			tmpProvider.saveConfigAsync(tmpProvider.state.WriteScope)
				.then(() => fDone(`Saved to ${tmpProvider._scopeLabel(tmpProvider.state.WriteScope)}.`)).catch(fFail);
		}
	}

	/** Route to the clone workflow (Part E). */
	openClone()
	{
		if (this.pict.providers.PlatformConfigurationRouter && this.pict.providers.PictRouter)
		{
			window.location.hash = '#/ConfigClone';
		}
	}
}

module.exports = ConfigViewer;

module.exports.default_configuration =
{
	ViewIdentifier: 'ConfigViewer',
	DefaultRenderable: 'ConfigViewerWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,
	CSS: /*css*/`
		.cv-wrap { padding: 1.25rem 1.5rem 2rem; }
		.cv-head { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; margin:0 0 1rem; }
		.cv-title { font-size:1.5rem; font-weight:700; line-height:1.2; margin:0; color:var(--theme-color-text-primary,#1f2733); }
		.cv-subtitle { font-size:0.9rem; color:var(--theme-color-text-muted,#6b7686); margin:0.2rem 0 0; }
		.cv-cols { display:grid; grid-template-columns: 16rem 1fr 17rem; gap:1.1rem; align-items:start; }
		@media (max-width: 1100px) { .cv-cols { grid-template-columns: 14rem 1fr; } .cv-help-panel { display:none; } }
		.cv-rail { border:1px solid var(--theme-color-border-default,#d7dce3); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:0.6rem; }
		.cv-rail-group { margin:0 0 0.7rem; }
		.cv-rail-head { font-size:0.66rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--theme-color-text-muted,#6b7686); padding:0.25rem 0.5rem; }
		.cv-rail-item { padding:0.4rem 0.6rem; border-radius:6px; font-size:0.9rem; color:var(--theme-color-text-secondary,#45505f); cursor:pointer; }
		.cv-rail-item:hover { background:var(--theme-color-background-secondary,#eef1f6); color:var(--theme-color-text-primary,#1f2733); }
		.cv-rail-item.is-active { background:var(--theme-color-brand-primary,#2e7d74); color:#fff; font-weight:600; }
		.cv-main { border:1px solid var(--theme-color-border-default,#d7dce3); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:1.1rem 1.25rem; min-height:20rem; }
		.cv-editor-head { display:flex; justify-content:space-between; align-items:center; gap:1rem; flex-wrap:wrap; margin:0 0 0.6rem; }
		.cv-editor-title { font-size:1.05rem; font-weight:700; color:var(--theme-color-text-primary,#1f2733); }
		.cv-value-select, .cv-scope-select { font-size:0.85rem; padding:0.2rem 0.4rem; border-radius:6px; border:1px solid var(--theme-color-border-default,#d7dce3); }
		.cv-scopebar { display:flex; align-items:center; gap:0.6rem; flex-wrap:wrap; padding:0.5rem 0.7rem; margin:0 0 0.7rem; border-radius:7px; background:var(--theme-color-background-tertiary,#f4f6f9); border:1px solid var(--theme-color-border-light,#e8ebf0); }
		.cv-scopelabel { font-size:0.78rem; font-weight:600; color:var(--theme-color-text-secondary,#45505f); }
		.cv-legend { display:flex; gap:0.35rem; flex-wrap:wrap; margin-left:auto; }
		.cv-legend-item { font-size:0.6rem; font-weight:600; padding:0 0.42rem; border-radius:9px; line-height:1.6; }
		.cv-editor-host { border:1px solid var(--theme-color-border-light,#e8ebf0); border-radius:7px; padding:0.5rem 0.4rem; min-height:9rem; background:var(--theme-color-background-secondary,#fbfcfd); }
		.cv-savebar { margin:0.8rem 0 0; display:flex; align-items:center; gap:0.8rem; }
		.cv-readonly { font-size:0.85rem; color:var(--theme-color-text-muted,#6b7686); }
		.cv-help-panel { border:1px solid var(--theme-color-border-default,#d7dce3); border-radius:8px; background:var(--theme-color-background-panel,#fff); padding:0.9rem 1rem; }
		.cv-help-head { font-size:0.68rem; font-weight:700; text-transform:uppercase; letter-spacing:0.05em; color:var(--theme-color-brand-primary,#2e7d74); margin:0 0 0.5rem; }
		.cv-help-ns { font-size:0.86rem; color:var(--theme-color-text-secondary,#45505f); line-height:1.45; margin:0 0 0.7rem; }
		.cv-help-key { margin:0 0 0.55rem; }
		.cv-help-key-addr { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size:0.72rem; font-weight:600; color:var(--theme-color-text-primary,#1f2733); }
		.cv-help-key-text { font-size:0.8rem; color:var(--theme-color-text-muted,#6b7686); line-height:1.4; }
		.cv-empty { padding:2.5rem 1rem; text-align:center; color:var(--theme-color-text-muted,#6b7686); }
		.cv-spinner { display:inline-block; width:1.6rem; height:1.6rem; border:3px solid var(--theme-color-border-default,#dbdbdb); border-top-color:var(--theme-color-brand-primary,#2e7d74); border-radius:50%; animation:cv-spin 0.8s linear infinite; }
		@keyframes cv-spin { to { transform: rotate(360deg); } }
	`,
	Templates:
	[
		{
			Hash: 'ConfigViewer-Wrap',
			Template: /*html*/`
				<div class="cv-wrap">
					<div class="cv-head">
						<div>
							<h1 class="cv-title">Configuration</h1>
							<p class="cv-subtitle">See where each setting comes from across scopes, edit it at the right level, and clone it between contexts.</p>
						</div>
						<button class="button is-light" onclick="_Pict.views.ConfigViewer.openClone()"><span class="icon">{~I:Copy~}</span><span>Clone…</span></button>
					</div>
					{~TS:ConfigViewer-Message:AppData.ConfigViewer.MessageSlot~}
					<div class="cv-cols">
						<div class="cv-rail">{~TS:ConfigViewer-RailGroup:AppData.ConfigViewer.RailGroups~}</div>
						<div class="cv-main">
							{~TS:ConfigViewer-Loading:AppData.ConfigViewer.LoadingSlot~}
							{~TS:ConfigViewer-Editor:AppData.ConfigViewer.SelectedSlot~}
							{~TS:ConfigViewer-Empty:AppData.ConfigViewer.EmptySlot~}
						</div>
						{~TS:ConfigViewer-HelpPanel:AppData.ConfigViewer.HelpSlot~}
					</div>
				</div>`
		},
		{
			Hash: 'ConfigViewer-RailGroup',
			Template: /*html*/`
				<div class="cv-rail-group">
					<div class="cv-rail-head">{~D:Record.Group~}</div>
					{~TS:ConfigViewer-RailItem:Record.Items~}
				</div>`
		},
		{
			Hash: 'ConfigViewer-RailItem',
			Template: /*html*/`<div class="cv-rail-item {~D:Record.ActiveClass~}" onclick="_Pict.views.ConfigViewer.selectRailItem('{~D:Record.Kind~}','{~D:Record.NamespaceKey~}','{~D:Record.Entity~}','{~D:Record.RecordID~}')">{~D:Record.Label~}</div>`
		},
		{
			Hash: 'ConfigViewer-Editor',
			Template: /*html*/`
				<div class="cv-editor-head">
					<div class="cv-editor-title">{~D:Record.Label~}</div>
					{~TS:ConfigViewer-ValuePicker:AppData.ConfigViewer.ValuePickerSlot~}
				</div>
				{~TS:ConfigViewer-ScopeBar:AppData.ConfigViewer.ScopePickerSlot~}
				{~TS:ConfigViewer-EditorBody:AppData.ConfigViewer.EditorBodySlot~}
				{~TS:ConfigViewer-NoValues:AppData.ConfigViewer.NoValuesSlot~}`
		},
		{
			Hash: 'ConfigViewer-EditorBody',
			Template: /*html*/`
				<div id="CV-EditorHost" class="cv-editor-host"></div>
				<div class="cv-savebar">
					{~TS:ConfigViewer-SaveBtn:AppData.ConfigViewer.SaveEnabledSlot~}
					{~TS:ConfigViewer-ReadOnly:AppData.ConfigViewer.ReadOnlySlot~}
				</div>`
		},
		{
			Hash: 'ConfigViewer-NoValues',
			Template: /*html*/`<div class="cv-empty"><span class="icon is-large">{~I:Info~}</span><p class="mt-3">Nothing is configured for this namespace at the current context yet. Values appear here once they’ve been set at some scope.</p></div>`
		},
		{
			Hash: 'ConfigViewer-ValuePicker',
			Template: /*html*/`<select class="cv-value-select" onchange="_Pict.views.ConfigViewer.selectValue(this.value)">{~TS:ConfigViewer-ValueOption:AppData.ConfigViewer.ValueOptions~}</select>`
		},
		{
			Hash: 'ConfigViewer-ValueOption',
			Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~}>{~D:Record.Value~}</option>`
		},
		{
			Hash: 'ConfigViewer-ScopeBar',
			Template: /*html*/`
				<div class="cv-scopebar">
					<span class="cv-scopelabel">Edits write to:</span>
					<select class="cv-scope-select" onchange="_Pict.views.ConfigViewer.setWriteScope(this.value)">{~TS:ConfigViewer-ScopeOption:AppData.ConfigViewer.ScopeOptions~}</select>
					<span class="cv-legend">{~TS:ConfigViewer-LegendItem:AppData.ConfigViewer.Legend~}</span>
				</div>`
		},
		{
			Hash: 'ConfigViewer-ScopeOption',
			Template: /*html*/`<option value="{~D:Record.Value~}" {~D:Record.SelectedAttr~} {~D:Record.DisabledAttr~}>{~D:Record.Label~}</option>`
		},
		{
			Hash: 'ConfigViewer-LegendItem',
			Template: /*html*/`<span class="cv-legend-item {~D:Record.BadgeClass~}">{~D:Record.Label~}</span>`
		},
		{
			Hash: 'ConfigViewer-SaveBtn',
			Template: /*html*/`<button class="button is-link" onclick="_Pict.views.ConfigViewer.save()"><span class="icon">{~I:Save~}</span><span>{~D:Record.SaveLabel~}</span></button>`
		},
		{
			Hash: 'ConfigViewer-ReadOnly',
			Template: /*html*/`<span class="cv-readonly">Read-only — you don’t have write access at this scope.</span>`
		},
		{
			Hash: 'ConfigViewer-HelpPanel',
			Template: /*html*/`
				<div class="cv-help-panel">
					<div class="cv-help-head">What this means</div>
					<p class="cv-help-ns">{~D:AppData.ConfigViewer.HelpNamespace~}</p>
					{~TS:ConfigViewer-HelpKey:AppData.ConfigViewer.HelpKeyList~}
				</div>`
		},
		{
			Hash: 'ConfigViewer-HelpKey',
			Template: /*html*/`<div class="cv-help-key"><div class="cv-help-key-addr">{~D:Record.Address~}</div><div class="cv-help-key-text">{~D:Record.Text~}</div></div>`
		},
		{
			Hash: 'ConfigViewer-Message',
			Template: /*html*/`<div class="notification is-warning is-soft py-2 px-3 mb-3">{~D:Record.Message~}</div>`
		},
		{
			Hash: 'ConfigViewer-Loading',
			Template: /*html*/`<div class="cv-empty"><span class="cv-spinner"></span><p class="mt-3">Loading…</p></div>`
		},
		{
			Hash: 'ConfigViewer-Empty',
			Template: /*html*/`<div class="cv-empty"><span class="icon is-large">{~I:Settings~}</span><p class="mt-3">Pick a configuration namespace from the left to see where its values come from.</p></div>`
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'ConfigViewerWrap',
			TemplateHash: 'ConfigViewer-Wrap',
			ContentDestinationAddress: '#PlatformConfiguration-Application-Container',
		}
	]
};
